import { useCallback, useMemo, useRef } from 'react';

import { useDroppable } from '@dnd-kit/core';
import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual';

import { INVENTORY_CARD_HEIGHT } from '../lib/constants';
import {
    getInventoryDropzoneId,
    getInventorySortableId,
    INVENTORY_DROPZONE_DND_TYPE,
} from '../lib/dnd';

import type { Warehouse } from '@entities/equipment';

interface UseWarehouseInventoryDndParams {
    warehouseId: number;
    warehouseItems: Warehouse['items'];
    inventoryItems?: Warehouse['items'];
    activeInventoryGuid?: string | null;
}

export const useWarehouseInventoryDnd = ({
    warehouseId,
    warehouseItems,
    inventoryItems,
    activeInventoryGuid,
}: UseWarehouseInventoryDndParams) => {
    const cardsContainerRef = useRef<HTMLDivElement>(null);
    const displayedInventoryItems = inventoryItems ?? warehouseItems;
    const hasInventoryItems = displayedInventoryItems.length > 0;
    const inventorySortableIds = useMemo(
        () => displayedInventoryItems.map((item) => getInventorySortableId(item.guid)),
        [displayedInventoryItems]
    );
    const activeInventoryIndex = displayedInventoryItems.findIndex(
        (item) => item.guid === activeInventoryGuid
    );
    const { setNodeRef: setInventoryDropzoneRef } = useDroppable({
        id: getInventoryDropzoneId(warehouseId),
        data: {
            type: INVENTORY_DROPZONE_DND_TYPE,
            warehouseId,
        },
    });
    const setCardsContainerNode = useCallback(
        (node: HTMLDivElement | null) => {
            cardsContainerRef.current = node;

            if (hasInventoryItems) {
                setInventoryDropzoneRef(node);
            }
        },
        [hasInventoryItems, setInventoryDropzoneRef]
    );
    const inventoryVirtualizer = useVirtualizer({
        count: displayedInventoryItems.length,
        getScrollElement: () => cardsContainerRef.current,
        estimateSize: () => INVENTORY_CARD_HEIGHT,
        getItemKey: (index) => displayedInventoryItems[index]?.guid ?? index,
        overscan: 3,
        rangeExtractor: useCallback(
            (range) => {
                const visibleIndexes = defaultRangeExtractor(range);

                if (activeInventoryIndex === -1 || visibleIndexes.includes(activeInventoryIndex)) {
                    return visibleIndexes;
                }

                return [...visibleIndexes, activeInventoryIndex].sort(
                    (left, right) => left - right
                );
            },
            [activeInventoryIndex]
        ),
    });

    return {
        displayedInventoryItems,
        hasInventoryItems,
        inventorySortableIds,
        setInventoryDropzoneRef,
        setCardsContainerNode,
        virtualItems: inventoryVirtualizer.getVirtualItems(),
        virtualTotalSize: inventoryVirtualizer.getTotalSize(),
    };
};
